/**
 * "Runtime API reference" — Reference section (B27.5, 2026-06-12).
 *
 * The customer-side runtime contracts that previously had no reference
 * pages (every integration reverse-engineered source): the intent-
 * verification header (payload fields, verification rules, and the exact
 * failure reasons from the shared verify core), the act-as-user runtime
 * contract as the customer's backend observes it, and the witness (Pipe B)
 * payload contract — shape descriptors, never values. Field names, reason
 * strings, and shape categories are lifted verbatim from the sources
 * (sdk-* verify cores; packages/api-witness shape inferrer).
 */
import type { DocPageContent } from '../types';

const REPORT_OUTCOME = `POST https://api.syncanix.com/api/conversation-outcomes
x-syncanix-api-key: gak_…          // a SECRET key; a publishable pk_ is refused
content-type: application/json

{
  "environment": "production",     // optional; defaults to production
  "outcomes": [
    { "conversationId": "conv_0f3a…", "outcomeKey": "purchase" },
    { "conversationId": "9c21…",      "outcomeKey": "ticket_closed" }
  ]
}

// 200
{ "recorded": 2, "unknownConversations": [] }`;

const REPORT_OUTCOME_SDK = `import { reportOutcomes } from '@syncanix/sdk-node';

await reportOutcomes({
  apiKey: process.env.SYNCANIX_API_KEY,
  outcomes: [{ conversationId: order.syncanixConversationId, outcomeKey: 'purchase' }],
});`;

const WIRE_FORMAT = `X-Syncanix-Intent: base64url(JSON({ "payload": <obj>, "signature": "<hex>" }))
payload   = { v: 2, toolCallId, tenantId, sub, aud, operation, argsHash, nonce, issuedAt, expiresAt, requiresStepUp? }
signature = hex(HMAC-SHA256(JSON(payload), secret))

// v1 (legacy) payloads — { toolCallId, tenantId, userId?, method, path, issuedAt,
// expiresAt } with no "v" field — remain accepted during the dual-accept window.`;

export const REFERENCE_RUNTIME_API_PAGE: DocPageContent = {
  slug: 'reference/runtime-api',
  titleKey: 'docs.reference.runtime.title',
  descriptionKey: 'docs.reference.runtime.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.reference.runtime.lead' },

    { kind: 'heading', level: 2, id: 'intent', textKey: 'docs.reference.runtime.intent.heading' },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.intent.body' },
    { kind: 'code', language: 'text', code: WIRE_FORMAT },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.intent.security' },
    {
      kind: 'definitions',
      variant: 'code',
      items: [
        {
          termKey: 'docs.reference.runtime.field.toolCallId.term',
          descKey: 'docs.reference.runtime.field.toolCallId.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.tenantId.term',
          descKey: 'docs.reference.runtime.field.tenantId.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.sub.term',
          descKey: 'docs.reference.runtime.field.sub.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.aud.term',
          descKey: 'docs.reference.runtime.field.aud.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.operation.term',
          descKey: 'docs.reference.runtime.field.operation.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.argsHash.term',
          descKey: 'docs.reference.runtime.field.argsHash.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.nonce.term',
          descKey: 'docs.reference.runtime.field.nonce.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.issuedExpires.term',
          descKey: 'docs.reference.runtime.field.issuedExpires.desc',
        },
        {
          termKey: 'docs.reference.runtime.field.requiresStepUp.term',
          descKey: 'docs.reference.runtime.field.requiresStepUp.desc',
        },
      ],
    },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.intent.v1note' },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.intent.failures' },

    {
      kind: 'heading',
      level: 2,
      id: 'act-as-user',
      textKey: 'docs.reference.runtime.actAsUser.heading',
    },
    {
      kind: 'paragraph',
      textKey: 'docs.reference.runtime.actAsUser.body',
      links: {
        guide: {
          target: { docSlug: 'guides/act-as-user' },
          labelKey: 'docs.reference.runtime.actAsUser.guideLink',
        },
      },
    },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.actAsUser.contract' },

    {
      kind: 'heading',
      level: 2,
      id: 'report-outcome',
      textKey: 'docs.reference.runtime.outcome.heading',
    },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.outcome.body' },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.outcome.inferred' },
    { kind: 'code', language: 'ts', code: REPORT_OUTCOME_SDK },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.outcome.http' },
    { kind: 'code', language: 'text', code: REPORT_OUTCOME },
    {
      kind: 'definitions',
      variant: 'code',
      items: [
        {
          termKey: 'docs.reference.runtime.outcome.field.conversationId.term',
          descKey: 'docs.reference.runtime.outcome.field.conversationId.desc',
        },
        {
          termKey: 'docs.reference.runtime.outcome.field.outcomeKey.term',
          descKey: 'docs.reference.runtime.outcome.field.outcomeKey.desc',
        },
        {
          termKey: 'docs.reference.runtime.outcome.field.environment.term',
          descKey: 'docs.reference.runtime.outcome.field.environment.desc',
        },
        {
          termKey: 'docs.reference.runtime.outcome.field.unknown.term',
          descKey: 'docs.reference.runtime.outcome.field.unknown.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.reference.runtime.outcome.idempotent.title',
      textKey: 'docs.reference.runtime.outcome.idempotent.body',
    },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.outcome.limits' },

    {
      kind: 'heading',
      level: 2,
      id: 'witness',
      textKey: 'docs.reference.runtime.witness.heading',
    },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.witness.body' },
    { kind: 'paragraph', textKey: 'docs.reference.runtime.witness.shapes' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.reference.runtime.witness.never.title',
      textKey: 'docs.reference.runtime.witness.never.body',
    },
  ],
};
